import { useState } from "react";
import { CheckCircle2, Upload, FileText, Eye, EyeOff, UserCheck, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import ProductionMarks from "./ProductionMarks";

/**
 * Reusable proofing surface that ALSO appears on the dashboard once a
 * designer-uploaded proof is ready.  Renders:
 *   - the file thumbnail (or PDF icon)
 *   - a toggle "View Production Marks" / "View Clean Proof"
 *   - two high-visibility decision buttons
 *
 * The actual approval/reupload network calls are passed in by the parent so
 * the same component can be used in BOTH the customer post-upload flow and
 * the dashboard proof review flow.
 */
export default function ProofingView({
  previewUrl,
  previewIsPdf = false,
  filename,
  widthIn = 3,
  heightIn = 2,
  shape = "Rectangle",
  onApprove,
  onReupload,
  approveLabel = "Approve & Proceed",
  reuploadLabel = "Re-upload",
  busy = false,
  testIdPrefix = "proof",
  helperNote,
  // Customer-facing framing — defaults match the historical "Proof Review"
  // dashboard usage. On the post-upload flow we override with "Live Preview"
  // so customers understand this is an instant mockup, NOT a designer proof
  // (designer proofs arrive by email/dashboard within 2 business hours).
  title = "Proof Review",
  subtitle = "Verify trim, bleed and safety areas before approving.",
}) {
  const [marks, setMarks] = useState(true);
  const [imgFailed, setImgFailed] = useState(false);
  // True when the customer's artwork is served as a raster (PNG/JPG) we can
  // safely scale with object-fit.  PDFs fall back to a file-card icon.
  const isRaster = !!previewUrl && !previewIsPdf;
  const isPdf = (filename || "").toLowerCase().endsWith(".pdf");

  // Match the SVG viewBox in <ProductionMarks/>:
  //   margin = bleedIn + 0.25" (so crop marks fit)
  // The stage extends to the same bleed-extended viewBox so the trim line
  // in the SVG aligns 1:1 with the inset rectangle we use to position the
  // customer's artwork.
  const bleedIn = 0.125;
  const margin = bleedIn + 0.25;
  const vbW = widthIn + 2 * margin;
  const vbH = heightIn + 2 * margin;
  const stageAspect = `${vbW} / ${vbH}`;
  const trimLeftPct = (margin / vbW) * 100;
  const trimTopPct = (margin / vbH) * 100;
  const trimWPct = (widthIn / vbW) * 100;
  const trimHPct = (heightIn / vbH) * 100;

  // Clip the artwork to the customer's selected shape so the proof shows
  // exactly what the die will cut.  Rectangle = no clip.
  const shapeKey = (shape || "").toLowerCase();
  const clipPath =
    shapeKey === "circle"
      ? "circle(50% at 50% 50%)"
      : shapeKey === "oval"
      ? "ellipse(50% 50% at 50% 50%)"
      : undefined;

  return (
    <div className="border border-slate-200 bg-white rounded-sm p-4 sm:p-6" data-testid={`${testIdPrefix}-view`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-display font-black text-slate-900 text-lg tracking-tight" data-testid={`${testIdPrefix}-title`}>{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {subtitle}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMarks((v) => !v)}
          className={`text-xs font-bold inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border transition-colors ${
            marks
              ? "bg-[#333F48] text-white border-[#333F48]"
              : "bg-white text-[#333F48] border-slate-300 hover:border-[#333F48]"
          }`}
          data-testid={`${testIdPrefix}-toggle-marks`}
        >
          {marks ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {marks ? "View Clean Proof" : "View Production Marks"}
        </button>
      </div>

      <div
        className="relative bg-slate-50 border border-slate-200 rounded-sm overflow-hidden mx-auto"
        style={{ aspectRatio: stageAspect, maxWidth: "640px" }}
        data-testid={`${testIdPrefix}-stage`}
      >
        {/* Artwork positioned EXACTLY at the trim area so it lines up with
            the SVG trim line on top.  object-fit: cover ensures the customer's
            design (whatever native size) fills the trim box. */}
        {isRaster ? (
          <div
            className="absolute"
            style={{
              left: `${trimLeftPct}%`,
              top: `${trimTopPct}%`,
              width: `${trimWPct}%`,
              height: `${trimHPct}%`,
              overflow: "hidden",
              clipPath,
              WebkitClipPath: clipPath,
            }}
          >
            <img
              src={previewUrl}
              alt={filename || "Proof"}
              className="block w-full h-full object-cover"
              onError={() => setImgFailed(true)}
            />
            {imgFailed && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-white text-center px-3"
                data-testid="proof-image-error"
              >
                <AlertTriangle className="w-6 h-6 text-amber-500" />
                <div className="text-[11px] font-bold text-slate-700">Proof preview didn&apos;t load</div>
                <div className="text-[10px] text-slate-500">Refresh the page, or message us and we&apos;ll resend it.</div>
              </div>
            )}
          </div>
        ) : (
          <div
            className="absolute"
            style={{
              left: `${trimLeftPct}%`,
              top: `${trimTopPct}%`,
              width: `${trimWPct}%`,
              height: `${trimHPct}%`,
            }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-2 bg-white">
              <FileText className="w-8 h-8" />
              <div className="text-[10px] font-semibold truncate max-w-[80%]">{filename || "Proof.pdf"}</div>
              <div className="text-[9px] uppercase tracking-widest">CMYK · Press-ready</div>
              {isPdf && previewUrl && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] font-bold text-[#333F48] hover:text-[#DD6B33]"
                >
                  Open the file →
                </a>
              )}
            </div>
          </div>
        )}
        <ProductionMarks widthIn={widthIn} heightIn={heightIn} shape={shape} show={marks} />
      </div>

      <div className="grid grid-cols-3 gap-3 text-[11px] text-slate-600 mt-4 max-w-2xl mx-auto">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 bg-[#333F48]" /> Trim line
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 bg-red-500" style={{ background: "repeating-linear-gradient(90deg,#ef4444 0,#ef4444 3px,transparent 3px,transparent 6px)" }} /> Bleed (0.125")
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 bg-emerald-600" style={{ background: "repeating-linear-gradient(90deg,#16a34a 0,#16a34a 3px,transparent 3px,transparent 6px)" }} /> Safe zone (0.125" in)
        </div>
      </div>

      {helperNote && (
        <div className="text-xs text-slate-600 bg-slate-50 border-l-2 border-[#333F48] px-3 py-2 mt-4 rounded-sm">
          {helperNote}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <button
          type="button"
          disabled={busy}
          onClick={onApprove}
          className="flex-1 h-12 rounded-sm bg-[#16a34a] text-white font-bold tracking-tight inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#15803d] transition-colors"
          data-testid={`${testIdPrefix}-approve-btn`}
        >
          <CheckCircle2 className="w-5 h-5" />
          {approveLabel}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onReupload}
          className="flex-1 h-12 rounded-sm bg-white text-[#0f172a] font-bold tracking-tight inline-flex items-center justify-center gap-2 border border-slate-300 disabled:opacity-50 hover:border-[#333F48] hover:text-[#333F48] transition-colors"
          data-testid={`${testIdPrefix}-reupload-btn`}
        >
          <Upload className="w-5 h-5" />
          {reuploadLabel}
        </button>
      </div>

      {/* Human-designer fallback — visible even after the automated proof renders. */}
      <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-600">
        <UserCheck className="w-3.5 h-3.5 text-[#333F48] flex-shrink-0" />
        <span>
          Not quite right?{" "}
          <Link
            to="/quote?topic=design-assist"
            className="font-bold text-[#333F48] hover:text-[#DD6B33] underline-offset-2 hover:underline"
            data-testid={`${testIdPrefix}-talk-to-designer`}
          >
            Talk to a Kolt designer
          </Link>{" "}
          — we'll review your file and prep a press-ready proof for you.
        </span>
      </div>

      <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-bold mt-4 text-center">
        Custom printing you&apos;ll actually love
      </div>
    </div>
  );
}
